import { computed, defineComponent, h } from 'vue'

import { date, QDate, QIcon, QPopupProxy } from 'quasar'

import BsInput from '../input/BsInput'
import { bsComponentProps, useBsComponent, ValidationRule } from '../useBsComponent'
import { useVm } from '../../../composables/useVm'

import { DATE_FORMAT } from '../../../constants'
import { quasarHandlers } from '../quasarHandlers'
import { useTemplateRef } from '../useTemplateRef'
import { emitModel } from '../emitModel'

const { formatDate, extractDate } = date

/**
 * A date field: a BsInput with a masked value and a calendar popup on its append slot.
 *
 * It renders another bundle component rather than a Quasar one, so the props it builds are BsInput's —
 * `required`, `autoSave` and `validation` are forwarded rather than acted on here, and the validation it
 * forwards is the caller's plus its own date check.
 */
export default defineComponent({
  name: 'BsDate',

  /**
   * Off, because this component's whole job is to REPLACE the model it is given.
   *
   * Vue 3 applies leftover `$attrs` to the rendered root on top of what the render set, and the root
   * here is the BsInput this hands a formatted date string to. `modelValue` -- a `Date` from the
   * caller's `v-model` -- is a leftover attr (the declared prop is `value`), so fallthrough put the
   * raw Date back over the string on every render. QInput's `date` mask could make nothing of it,
   * emitted `''` upward, and the empty value went round to the caller and cleared the field: a picked
   * release date vanished and the required-field guard kept complaining. `getRenderProps` already
   * forwards `$attrs` deliberately, so nothing is lost by not applying them a second time.
   */
  inheritAttrs: false,

  // See BsInput: undeclared DOM-named events are bound twice, once natively.
  emits: [ 'input', 'update:modelValue' ],

  props: {
    ...bsComponentProps,
    value: {
      type: [Date, String] as unknown as () => Date | string,
      default: void 0
    }
  },

  setup (props, { emit }) {
    // Template refs. A string `ref` in a render function does not populate `$refs` for a
    // component whose logic is in setup() -- see useTemplateRef.
    const { ref: BsDateProxy, set: setBsDateProxyRef } = useTemplateRef<any>()

    const vm = useVm()
    const bs = useBsComponent({ vm, props, emit, value: () => props.value })

    /**
     * `componentValue`, not `props.value`: `v-model` on a `bs-date` sends `modelValue` under Vue 3, so
     * the declared `value` prop is undefined for every caller in the apps and this resolved to nothing
     * -- an empty field whatever the book's release date was, and a calendar that opened on today.
     */
    const dateValue = computed(() => {
      const value = bs.componentValue.value as Date | string | undefined

      if (!value) return void 0

      if (typeof value === 'string') {
        return value
      }

      return formatDate(value, DATE_FORMAT)
    })

    /**
     * The date field is a masked free-text input, so a user can type a value that isn't a real date
     * (e.g. "0112/23/23"). Left unchecked that autosaves an Invalid Date up to the server. We inject a
     * validation rule (alongside any caller-supplied ones) so the component fails validation with a
     * clear message and, crucially, blocks the autosave before a bad date ever leaves the browser.
     * An empty value is allowed here — the separate `required` check owns the empty case.
     */
    const dateValidation = computed<ValidationRule[]>(() => {
      const isValidDate = (val: any) => {
        if (!val) return void 0
        const parsed = val instanceof Date ? val : new Date(val)
        return isNaN(parsed.getTime())
          ? { label: vm.$tc('system.errors.invalidDate') }
          : void 0
      }

      return [...(props.validation ?? []), isValidDate]
    })

    return {
      BsDateProxy,
      setBsDateProxyRef,
      ...bs,
      dateValue,
      dateValidation
    }
  },

  methods: {
    /** Renamed from `__renderScopedSlots`: Vue does not proxy setup/method keys beginning with `_`. */
    renderScopedSlots () {
      return {
        append: () => {
          // QIcon and QPopupProxy are components, so their children are slot functions.
          // The design system's calendar, in grey like the field's other icons.
          return (this.$attrs.disable as unknown as boolean) ? void 0 : h(QIcon, {
            name: 'app:calendar',
            size: '1.25rem',
            color: 'bs-grey-m'
          }, { default: () => [
            h(QPopupProxy, {
              ref: this.setBsDateProxyRef
            }, { default: () => [
              h(QDate, {
                // Quasar 2's v-model prop and event; the bundle's own API stays `value` / `input`.
                modelValue: this.dateValue,
                color: 'bs-g',
                minimal: true,
                options: this.$attrs.options,
                ['onUpdate:modelValue']: (date: string) => {
                    (this.BsDateProxy as QPopupProxy).hide()
                    emitModel(this, extractDate(date, DATE_FORMAT))
                    this.doAutoSave()
                  }
              })
            ] })
          ] })
        }
      }
    }
  },

  render () {
    // The date picker will show 01/01/1970 if we have a null value so make sure it's undefined instead.
    // `componentValue` for the same reason as `dateValue` above: a `v-model` caller sends `modelValue`.
    if (this.componentValue === null) emitModel(this, void 0)

    const props = {
      required: this.required,
      autoSave: this.autoSave,
      disable: this.$attrs.disable,
      mask: 'date',
      rules: ['date'],
      validation: this.dateValidation
    }

    const scopedSlots = {
      ...this.renderScopedSlots()
    }

    /**
     * The caller's test hooks, handed on by hand.
     *
     * `inheritAttrs: false` (above) keeps a caller's `modelValue` off the root, but it takes `data-cy`
     * with it -- and `getRenderProps` strips every `data-*` on purpose (see `withoutTestHooks`), so a
     * `data-cy` on a `<bs-date>` reached the page nowhere at all. The Apple Books codes spec was the
     * first to look for one. Given to BsInput, they fall through to ITS root exactly once, which is
     * where Vue 2 put them.
     */
    const testHooks = Object.fromEntries(
      Object.entries(this.$attrs).filter(([key]) => key.startsWith('data-'))
    )

    return h(BsInput, {
      // 'bs-date' was a `staticClass` alongside the dynamic one; Vue 3 merges both into `class`.
      class: ['bs-date', this.getClasses()],
      ...this.getRenderProps(props),
      ...quasarHandlers(this.getListeners()),
      ...this.getAttributes(),
      ...testHooks,
      /**
       * The FORMATTED string, under both names, last so it wins.
       *
       * `getRenderProps` spreads this component's `$attrs`, which under Vue 3 carries the caller's
       * `modelValue` -- a `Date` -- straight through to the masked text input inside. QInput could make
       * nothing of a Date against the `date` mask, emitted `''` back up, and that empty value went
       * round to the caller and cleared the field: picking a release date left the box blank and the
       * required-field guard still complaining. `value` alone did not fix it, because `componentValue`
       * reads `modelValue` first and BsInput was getting both.
       *
       * `''` and not `undefined` for the empty case, which is the other half of the same clobber:
       * QInput's `date` mask normalises whatever it is given and emits the result back when it differs,
       * so an undefined model produced an `update:modelValue` of `''` -- arriving just after the picker
       * had emitted the chosen date, and overwriting it. An empty string masks to an empty string and
       * QInput stays quiet.
       */
      modelValue: this.dateValue ?? '',
      value: this.dateValue ?? ''
    }, {
      ...this.getSlots(scopedSlots),
      // Vue 3 takes ONE slots object; the children this used to pass as a fourth argument are the
      // default slot.
      default: () => this.slot(this, 'default')
    })
  }
})
